import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, Loader2, MapPin, AlertTriangle } from "lucide-react";
import { StatusBadge, PaymentBadge, GetDirectionsButton } from "./JobActionsMenu";
import JobActionsMenu from "./JobActionsMenu";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
                     "July", "August", "September", "October", "November", "December"];

// iter98 — Calendar status filter tabs. Maps directly to the backend
// `status` query param on /admin/calendar/month + /admin/bookings/by-date.
const STATUS_TABS = [
  { key: "all",       label: "All Bookings",   testId: "cal-filter-all" },
  { key: "paid",      label: "Paid",           testId: "cal-filter-paid" },
  { key: "pending",   label: "Payment Pending", testId: "cal-filter-pending" },
  { key: "completed", label: "Completed",      testId: "cal-filter-completed" },
  { key: "cancelled", label: "Cancelled",      testId: "cal-filter-cancelled" },
];

// iter98 — Payment states that mean "dispatchable" (paid or autopay-guaranteed).
// Anything else is "pending" and triggers the "Do Not Dispatch Until Paid" banner
// on/after the service date.
const DISPATCHABLE_PAYMENT_STATUSES = new Set(["paid", "autopay_scheduled"]);

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function CalendarMonthView() {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(isoDate(today));
  const [dayJobs, setDayJobs] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const loadMonth = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/calendar/month", {
        params: {
          year: cursor.getFullYear(),
          month: cursor.getMonth() + 1,
          include_cancelled: showCancelled || statusFilter === "cancelled",
          status: statusFilter,
        },
      });
      setData(d);
    } finally { setLoading(false); }
  };

  const loadDay = async (date) => {
    setDayLoading(true);
    try {
      const { data: d } = await api.get(`/admin/bookings/by-date/${date}`, {
        params: {
          include_cancelled: showCancelled || statusFilter === "cancelled",
          status: statusFilter,
        },
      });
      setDayJobs(d.bookings || []);
    } finally { setDayLoading(false); }
  };

  useEffect(() => { loadMonth(); }, [cursor, showCancelled, statusFilter]);
  useEffect(() => { loadDay(selectedDate); }, [selectedDate, showCancelled, statusFilter]);

  const byDate = useMemo(() => {
    const m = {};
    (data?.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [data]);

  // Build calendar grid (6 rows × 7 cols)
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startOffset = firstOfMonth.getDay(); // 0..6 = Sun..Sat
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let i = 1; i <= daysInMonth; i++) {
    cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
  }
  while (cells.length < 42) cells.push(null);

  const todayIso = isoDate(today);

  return (
    <div className="space-y-4" data-testid="admin-calendar">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-xl font-extrabold tracking-tight font-[Outfit]" data-testid="cal-month-label">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setCursor(new Date(today.getFullYear(), today.getMonth(), 1)); setSelectedDate(todayIso); }} data-testid="cal-today">
            Today
          </Button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="show-cancelled-toggle-wrap">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              data-testid="show-cancelled-toggle"
            />
            <span className="text-[13px] font-bold text-gray-800">Show cancelled jobs</span>
          </label>
        </div>
      </div>

      {/* iter98 — Calendar status filter tabs. All Bookings by default so
          the admin sees every job on the master schedule. Route Map / Today's
          Jobs / Dispatch remain paid-only regardless of this filter. */}
      <div className="flex items-center gap-1.5 flex-wrap border-b border-gray-200 pb-2" data-testid="cal-status-filter">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatusFilter(t.key)}
            data-testid={t.testId}
            className={`px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider transition-all ${
              statusFilter === t.key
                ? "bg-[#047857] text-white shadow-sm"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Grid — Jobber-style clean cells */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6 min-h-[520px] relative">
          {loading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10"><Loader2 className="w-6 h-6 animate-spin text-[#047857]" /></div>}
          {cells.map((c, idx) => {
            if (!c) return <div key={idx} className="border-b border-r border-gray-100 bg-gray-50/50" />;
            const iso = isoDate(c);
            const info = byDate[iso];
            const isToday = iso === todayIso;
            const isSelected = iso === selectedDate;
            const count = info?.total || 0;
            const revenue = info?.revenue || 0;
            return (
              <button key={idx} onClick={() => setSelectedDate(iso)}
                data-testid={`cal-cell-${iso}`}
                className={`relative text-left p-2.5 border-b border-r border-gray-100 transition-all hover:bg-gray-50 ${isSelected ? "bg-emerald-50/60 ring-2 ring-inset ring-[#047857]" : ""} ${isToday && !isSelected ? "bg-amber-50/40" : ""}`}>
                <div className={`text-[13px] font-extrabold ${isToday ? "text-[#047857]" : "text-gray-800"}`}>{c.getDate()}</div>
                {count > 0 && (
                  <div className="mt-2 space-y-0.5" data-testid={`cal-day-summary-${iso}`}>
                    <div className="text-[12px] font-extrabold text-gray-900 tabular-nums" data-testid={`cal-jobs-${iso}`}>
                      {count} {count === 1 ? "Job" : "Jobs"}
                    </div>
                    <div className="text-[11px] font-bold text-gray-500 tabular-nums" data-testid={`cal-revenue-${iso}`}>
                      ${revenue.toFixed(0)}
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* DAY PANEL */}
      <DayJobsPanel
        date={selectedDate}
        jobs={dayJobs}
        loading={dayLoading}
        todayIso={todayIso}
        onClose={() => setSelectedDate(todayIso)}
        onUpdated={(movedBooking) => {
          loadMonth();
          // If reschedule returned a booking with a different preferred_date, jump
          // the calendar to that month + select the new date.
          if (movedBooking && movedBooking.preferred_date && movedBooking.preferred_date !== selectedDate) {
            const [y, m] = movedBooking.preferred_date.split("-").map(Number);
            if (y && m && (y !== cursor.getFullYear() || m - 1 !== cursor.getMonth())) {
              setCursor(new Date(y, m - 1, 1));
            }
            setSelectedDate(movedBooking.preferred_date);
          } else {
            loadDay(selectedDate);
          }
        }}
      />
    </div>
  );
}

function DayJobsPanel({ date, jobs, loading, todayIso, onUpdated }) {
  const totalRevenue = (jobs || []).reduce((s, j) => s + (j.price || 0), 0);
  // iter98 — "Do Not Dispatch Until Paid" warning trigger: date is today
  // or in the past, payment isn't in a dispatchable state, and the job
  // hasn't been completed/cancelled.
  const shouldWarnDispatch = (j) => {
    if (!j) return false;
    const paid = DISPATCHABLE_PAYMENT_STATUSES.has((j.payment_status || "").toLowerCase());
    const st = (j.status || "").toLowerCase();
    if (paid) return false;
    if (st === "completed" || st === "cancelled" || st === "canceled") return false;
    return (j.preferred_date || "") <= (todayIso || "");
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="day-jobs-panel">
      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Day Details</div>
            <div className="text-base font-extrabold tracking-tight font-[Outfit] text-gray-900" data-testid="day-panel-date">{date}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Day Revenue</div>
            <div className="text-lg font-extrabold tabular-nums text-[#047857]" data-testid="day-panel-revenue">${totalRevenue.toFixed(2)}</div>
          </div>
        </div>
        <div className="divide-y divide-gray-100 max-h-[480px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (jobs || []).length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500" data-testid="day-panel-empty">No jobs scheduled for this day.</div>
          ) : (jobs || []).map((j) => (
            <div key={j.booking_id} className="px-4 py-3 hover:bg-gray-50" data-testid={`day-job-${j.booking_id}`}>
              {shouldWarnDispatch(j) && (
                <div className="mb-2 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-800"
                     data-testid={`do-not-dispatch-${j.booking_id}`}>
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  <span className="text-[12px] font-extrabold uppercase tracking-wider">Do Not Dispatch Until Paid</span>
                </div>
              )}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{j.preferred_time}</div>
                    <StatusBadge status={j.status} />
                    <PaymentBadge paymentStatus={j.payment_status} />
                  </div>
                  <div className="text-[14px] font-extrabold text-gray-900 truncate">{j.service_name}</div>
                  <div className="text-[12px] text-gray-600 truncate">{j.name} · {j.phone || "—"}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.address || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1 justify-end">
                    <div className="text-base font-extrabold tabular-nums text-gray-900">${(j.price || 0).toFixed(2)}</div>
                    <JobActionsMenu booking={j} onUpdated={onUpdated} />
                  </div>
                  <div className="mt-1.5"><GetDirectionsButton address={j.address} className="!text-[#047857] !bg-emerald-50 !border-emerald-200" /></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAP — iter168: show ALL scheduled jobs regardless of payment status. */}
      <RouteMapPanel jobs={jobs || []} date={date} />
    </div>
  );
}

function RouteMapPanel({ jobs, date }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  // Build a multi-stop directions URL — Google Maps Embed API supports `directions` mode.
  // Use up to 9 waypoints (Google's limit for free embed).
  const stops = (jobs || []).filter((j) => j.address).slice(0, 10);
  let embedSrc = null;
  if (apiKey && stops.length === 1) {
    embedSrc = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(stops[0].address)}`;
  } else if (apiKey && stops.length >= 2) {
    const origin = encodeURIComponent(stops[0].address);
    const destination = encodeURIComponent(stops[stops.length - 1].address);
    const wpts = stops.slice(1, -1).map((s) => encodeURIComponent(s.address)).join("|");
    embedSrc = `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${origin}&destination=${destination}&mode=driving${wpts ? `&waypoints=${wpts}` : ""}`;
  }
  const openInMaps = stops.length > 0
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stops[stops.length - 1].address)}${stops.length > 1 ? `&waypoints=${stops.slice(0, -1).map((s) => encodeURIComponent(s.address)).join("|")}` : ""}`
    : null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" data-testid="route-map-panel">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Route Map</div>
          <div className="text-base font-extrabold tracking-tight font-[Outfit] text-gray-900">{stops.length} stops</div>
        </div>
        {openInMaps && (
          <a href={openInMaps} target="_blank" rel="noopener noreferrer"
            data-testid="route-map-open-in-maps"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-[#047857] border border-emerald-200 text-[10px] font-extrabold uppercase tracking-[0.14em]">
            <MapPin className="w-3 h-3" /> Open in Maps
          </a>
        )}
      </div>
      <div className="aspect-[5/4] bg-gray-50 relative" data-testid="route-map-iframe-wrapper">
        {embedSrc ? (
          <iframe
            title="Route map"
            src={embedSrc}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full border-0"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 text-center px-6">
            {!apiKey
              ? "Google Maps API key not configured."
              : "No jobs with addresses on this date."}
          </div>
        )}
      </div>
    </div>
  );
}
